import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import conditionContent from "../data/conditionContent";
import localizeCondition from "../data/localizeCondition";

export default function ConditionPage({ slug }) {
  const { t, i18n } = useTranslation();
  const source = conditionContent.find((c) => c.slug === slug);

  if (!source) {
    return (
      <>
        <Header />
        <main className="condition-page">
          <div className="container" style={{ padding: "80px 20px", textAlign: "center" }}>
            <h1>{t("condition.not_found")}</h1>
            <Link to="/">← {t("condition.back")}</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const condition = localizeCondition(source, i18n.language);

  const canonicalUrl = `https://ninoa.space/${condition.slug}`;
  const pageTitle = `${condition.title} | Ninoa`;

  const relatedConditions = conditionContent
    .filter((c) => c.slug !== condition.slug)
    .slice(0, 4)
    .map((c) => localizeCondition(c, i18n.language));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    name: source.name,
    description: source.intro,
    associatedAnatomy: {
      "@type": "AnatomicalStructure",
      name: "Skin",
    },
    possibleTreatment: {
      "@type": "MedicalTherapy",
      name: `Trigger tracking with Ninoa — identify personal ${source.name.toLowerCase()} triggers`,
    },
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={condition.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={condition.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={condition.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="condition-page">
        <div className="container condition-page-inner">
          <Link to="/" className="condition-back-link">
            <span aria-hidden="true" className="condition-back-arrow">←</span>
            <span>{t("condition.back")}</span>
          </Link>

          <h1 className="condition-page-title">{condition.title}</h1>
          <p className="condition-page-intro">{condition.intro}</p>

          <div className="condition-stat-pill">{condition.stats}</div>

          <section className="condition-section-card">
            <h2>{t("condition.how_title", { name: condition.name })}</h2>
            <ul className="condition-bullets">
              {condition.howNinoaHelps.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="condition-section-card">
            <h2>{t("condition.triggers_title", { name: condition.name })}</h2>
            <div className="condition-chips">
              {condition.commonTriggers.map((trigger) => (
                <span key={trigger} className="chip chip--trigger">
                  {trigger}
                </span>
              ))}
            </div>
          </section>

          <section className="condition-section-card">
            <h2>{t("condition.foods_title", { name: condition.name })}</h2>
            <div className="condition-chips">
              {condition.helperFoods.map((f) => (
                <span key={f} className="chip chip--helper">
                  {f}
                </span>
              ))}
            </div>
          </section>

          <section className="condition-cta-section">
            <h2>{condition.ctaText}</h2>
            <a
              href="https://apps.apple.com/app/id6755364999"
              className="condition-cta-btn"
            >
              {t("condition.download_cta")}
            </a>
            <p className="condition-cta-sub">{t("condition.app_store_sub")}</p>
          </section>

          <section className="condition-related">
            <h2 className="condition-related-title">{t("condition.related_title")}</h2>
            <p className="condition-related-sub">
              {t("condition.related_sub")}
            </p>
            <div className="condition-related-grid">
              {relatedConditions.map((c) => (
                <Link key={c.slug} to={`/${c.slug}`} className="condition-related-card">
                  <span className="condition-related-name">{c.name}</span>
                  <span className="condition-related-cta">{t("condition.learn_more")} <span aria-hidden="true">→</span></span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
